import { useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from './context/UserContext';
import Login from './pages/Login';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import FulfillmentHQ from './pages/FulfillmentHQ';
import Recurring from './pages/Recurring';
import ActiveBuilds from './pages/ActiveBuilds';
import PrivateNetwork from './pages/PrivateNetwork';
import WebsiteDetail from './pages/WebsiteDetail';
import Sales from './pages/Sales';
import Settings from './pages/Settings';
import Agents from './pages/Agents';
import Financials from './pages/Financials';
import Chat from './pages/Chat';
import Uptime from './pages/Uptime';
import Hosting from './pages/Hosting';
import Security from './pages/Security';
import HostingDetail from './pages/HostingDetail';
import HostingInventory from './pages/HostingInventory';
import ClientComms from './pages/ClientComms';
import Inbox from './pages/Inbox';
import UptimeDetail from './pages/UptimeDetail';
import Bookings from './pages/Bookings';
import Content from './pages/Content';
import Ideation from './pages/Ideation';
import ContentPiece from './pages/ContentPiece';
import ProtectedRoute from './components/auth/ProtectedRoute';
import TwoFactorGate from './components/auth/TwoFactorGate';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import BottomNavigation from './components/layout/BottomNavigation';
import ChatPanel from './components/chat/ChatPanel';
import PushPrompt from './components/push/PushPrompt';
import PullToRefresh from './components/layout/PullToRefresh';
import { emitAppRefresh, hasNewerBuild } from './lib/appRefresh';

// Initialize Font Awesome library
library.add(fas, far, fab);

function App() {
  const { isAuthenticated, user, getDefaultRoute, mfaState } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  // Bumped by pull-to-refresh: remounting the routes refetches the page.
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    // A pull is also how the home-screen app picks up a new deploy.
    if (await hasNewerBuild()) {
      window.location.reload();
      return;
    }
    emitAppRefresh();
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Redirect to default route based on user role when authenticated
  useEffect(() => {
    // Skip redirection if not authenticated or user data isn't loaded yet
    if (!isAuthenticated || !user) {
      return;
    }
    
    if (isAuthenticated && location.pathname === '/') {
      const defaultRoute = getDefaultRoute();
      navigate(defaultRoute, { replace: true });
    }
    
    // Always redirect client users if they try to access any restricted route
    if (user.role === 'client') {
      // Only allow clients to access these specific routes
      const allowedRoutes = [
        '/private-network',
        '/login'
      ];
      
      // Also allow access to detail pages
      const isDetailPage = 
        location.pathname.startsWith('/private-network/');
      
      // Redirect to private-network if on restricted route
      if (!allowedRoutes.includes(location.pathname) && !isDetailPage) {
        navigate('/private-network', { replace: true });
      }
    }
  }, [isAuthenticated, user, location.pathname, navigate, getDefaultRoute]);

  // Show loading state only if we're not on the login page
  if (isLoading && window.location.pathname !== '/login') {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--surface-canvas)' }}>
        <div className="text-center">
          <img src="/bullfinch-mark.svg" alt="" className="w-10 h-10 mx-auto mb-4" />
          <div className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  // Signed in but not past the code step: nothing else renders, not the chat or the pull-to-refresh either.
  // The database refuses below-aal2 reads anyway; this keeps the screens from loading half-empty.
  if (isAuthenticated && location.pathname !== '/login' && mfaState !== 'ok' && mfaState !== 'signed-out') {
    return <TwoFactorGate />;
  }

  return (
    <>
    {isAuthenticated && <ChatPanel />}
    {isAuthenticated && <PushPrompt />}
    {isAuthenticated && <PullToRefresh onRefresh={handleRefresh} />}
    <Routes key={refreshKey}>
      <Route path="/login" element={<Login />} />
      <Route
        path="/clients"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <Clients />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:clientId"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <ClientDetail />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <Sales />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/*"
        element={
          <ProtectedRoute>
            <>
              <Settings />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/*"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <>
              <Agents />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fulfillment"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <FulfillmentHQ />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/builds"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <ActiveBuilds />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <Recurring />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ideation"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <Ideation />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/content"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <Content />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/content/pieces/:pieceId"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <ContentPiece />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/financials"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <>
              <Financials />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <Chat />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:channelId"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'agency_owner']}>
            <>
              <Chat />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-comms"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'agency_owner']}>
            <>
              <ClientComms />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inbox"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'agency_owner']}>
            <>
              <Inbox />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/uptime"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'agency_owner']}>
            <>
              <Uptime />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hosting"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'agency_owner']}>
            <>
              <Hosting />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <>
              <Security />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hosting/inventory"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'agency_owner']}>
            <>
              <HostingInventory />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hosting/:accountId"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'agency_owner']}>
            <>
              <HostingDetail />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/uptime/:siteId"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'agency_owner']}>
            <>
              <UptimeDetail />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/private-network"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'client', 'agency_owner']}>
            <>
              <PrivateNetwork />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/private-network/:websiteId"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'authenticated', 'client', 'agency_owner']}>
            <>
              <WebsiteDetail />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute requiredRoles={['admin', 'developer', 'agency_owner']}>
            <>
              <Bookings />
              <BottomNavigation />
            </>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
      <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
    </Routes>
    </>
  );
}

export default App;
